import { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/index.js';
import { LandingIcon } from '../../ui/LandingIcon.js';
import { claimHref, OFFER, offerIsLive, rememberPromo } from './offer.js';
import { useHydrated, useRenderNow } from '../../lib/hydration.js';
import './scroll.css';

/**
 * The slim bar that keeps the sign-up one tap away once the hero has gone.
 *
 * It only says things that are true: the welcome offer from `offer.ts` while
 * that offer is live, and that signing up asks for no card. It steps aside
 * wherever the page already has its own call to action on screen, the hero,
 * the pricing cards, the closing section and the footer, because two buttons
 * asking for the same click at once read as pressure rather than help. It
 * also waits while the scratch card is open, so the offer is never on screen
 * twice. Closing it holds for the rest of the session: someone who said no
 * once should not be asked again on every scroll.
 */

const DISMISSED_KEY = 'krevya.landing.ctaBarDismissed';

/* What has its own CTA. Watched as whole sections, not their buttons, so the
   bar does not depend on markup inside sections other people are editing. */
const BLOCKERS = ['.lp-hero', '#pricing', '.lp-final', '.lp-footer'];

function wasDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function StickyOffer({
  tx,
  suppressed,
}: {
  tx: (en: string, tr: string) => string;
  /** True while the welcome card is open, so the offer is not shown twice. */
  suppressed: boolean;
}) {
  const t = useT();
  const live = offerIsLive(useRenderNow());
  const hydrated = useHydrated();
  const [dismissed, setDismissed] = useState(wasDismissed);
  // Starts as "something is in view": the page opens on the hero.
  const [blocked, setBlocked] = useState(true);
  const bar = useRef<HTMLElement>(null);

  useEffect(() => {
    if (dismissed) return;
    const inView = new Set<Element>();
    const targets = BLOCKERS.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector)),
    );
    if (targets.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) inView.add(entry.target);
        else inView.delete(entry.target);
      }
      setBlocked(inView.size > 0);
    });
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [dismissed]);

  const shown = !dismissed && !blocked && !suppressed;

  /*
   * The guide in the corner lifts out of the bar's way. It is told through an
   * attribute on the page rather than a prop because it is a sibling that owns
   * its own state, and the CSS can do the moving.
   */
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.lp');
    if (!root) return;
    if (shown) {
      root.dataset.ctabar = 'on';
      const height = bar.current?.offsetHeight ?? 0;
      root.style.setProperty('--ctabar-h', `${height}px`);
    } else {
      delete root.dataset.ctabar;
    }
    return () => {
      delete root.dataset.ctabar;
    };
  }, [shown]);

  // Kept, hidden, while the prerendered page is adopted: the build cannot know
  // this tab dismissed it, and the markup has to match what the build drew.
  if (dismissed && hydrated) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Blocked storage: it still closes for this page view.
    }
    setDismissed(true);
  };

  return (
    <aside
      ref={bar}
      className="lp-ctabar"
      data-shown={shown ? 'true' : 'false'}
      aria-label={tx('Sign up', 'Kayıt ol')}
      inert={!shown}
    >
      <p className="lp-ctabar-copy">
        {live ? (
          <>
            <b>
              {t('landing.offer.prize', { percent: String(OFFER.percent) })}
            </b>
            <span>
              <span className="lp-ctabar-long">{tx('with code ', 'kod: ')}</span>
              {/* The tag and the chip travel as one piece, so a line that
                  wraps never leaves the tag at the end of one line and the
                  code at the start of the next. */}
              <span className="lp-ctabar-code">
                <LandingIcon name="tag" />
                <code>{OFFER.code}</code>
              </span>
              <i aria-hidden="true">·</i>
              <NoCard tx={tx} />
            </span>
          </>
        ) : (
          <>
            <b>
              {tx('See the angles Krevya finds', 'Krevya’nın bulduğu açıları gör')}
            </b>
            <span>
              <NoCard tx={tx} />
            </span>
          </>
        )}
      </p>
      <a
        className="lp-button lp-ctabar-go"
        href={live ? claimHref() : '/register'}
        onClick={live ? rememberPromo : undefined}
      >
        <span className="lp-ctabar-long">{tx('Get started', 'Hemen başla')}</span>
        <span className="lp-ctabar-short">{tx('Start', 'Başla')}</span>
        <LandingIcon name="arrow" />
      </a>
      <button
        type="button"
        className="lp-ctabar-close"
        onClick={dismiss}
        aria-label={t('landing.nav.close')}
      >
        <LandingIcon name="cross" />
      </button>
    </aside>
  );
}

/* The full sentence where there is room, the short one on a phone. */
function NoCard({ tx }: { tx: (en: string, tr: string) => string }) {
  return (
    <>
      <span className="lp-ctabar-long">{tx('No card needed to sign up', 'Kayıt için kart gerekmez')}</span>
      <span className="lp-ctabar-short">{tx('No card needed', 'Kart gerekmez')}</span>
    </>
  );
}
